import React from 'react';
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Building2,
  ChevronRight,
  Sparkles,
  TrendingUp,
  X
} from 'lucide-react';
import { AiSourceBadge } from './AiUiShared';
import { buildAiLeadDetailPath, buildAiLeadsListPath } from './aiListDeepLinks';

const BULLET_ICONS = {
  overall: Sparkles,
  collection: BarChart3,
  forecast: TrendingUp,
  inventory: Building2,
  overdue: AlertTriangle
};

const METRIC_TONE_CLASS = {
  default: 'text-gray-900',
  warning: 'text-amber-700',
  success: 'text-emerald-700',
  info: 'text-indigo-700'
};

function normalizeBullet(bullet) {
  if (typeof bullet === 'string') {
    return { category: 'overall', text: bullet };
  }
  return {
    category: bullet?.category || 'overall',
    text: bullet?.text || ''
  };
}

function PortfolioHealthRing({ score }) {
  const safe = Math.max(0, Math.min(100, Number(score) || 0));
  const color =
    safe >= 70 ? 'text-emerald-600' : safe >= 45 ? 'text-amber-600' : 'text-red-600';
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50/60 to-white px-4 py-3">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
        Portfolio health
      </p>
      <p className={`text-3xl font-bold ${color}`}>{safe}</p>
      <p className="text-xs text-gray-500">/ 100</p>
    </div>
  );
}

function MetricTile({ metric, onNavigate }) {
  const toneClass = METRIC_TONE_CLASS[metric.tone] || METRIC_TONE_CLASS.default;
  const clickable = metric.aiFilter || metric.leadId;

  const body = (
    <>
      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
        {metric.label}
      </p>
      <p className={`mt-1 text-lg font-bold ${toneClass}`}>{metric.value}</p>
    </>
  );

  if (!clickable) {
    return (
      <div className="rounded-xl border border-gray-100 bg-white px-3 py-2.5 shadow-sm">
        {body}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onNavigate(metric)}
      className="group w-full rounded-xl border border-gray-100 bg-white px-3 py-2.5 text-left shadow-sm transition hover:border-teal-200 hover:bg-teal-50/40">
      {body}
      <span className="mt-1 inline-flex items-center gap-0.5 text-[10px] font-semibold text-[#1E4A53] opacity-0 transition group-hover:opacity-100">
        Open
        <ChevronRight className="h-3 w-3" />
      </span>
    </button>
  );
}

export default function AiPulseDetailDrawer({
  open,
  onClose,
  pulse,
  projectId,
  onNavigate,
  onRefresh,
  isRefreshing
}) {
  if (!open) return null;

  const details = pulse?.details;
  const bullets = (pulse?.bullets || []).map(normalizeBullet).filter((b) => b.text);
  const scope = details?.scope || {
    verticalLabel: pulse?.vertical || 'Dashboard',
    scopeLabel: pulse?.scopeLabel,
    levelLabel: pulse?.levelLabel,
    designationName: pulse?.designationName,
    pulseLevelTier: pulse?.pulseLevelTier,
    projectName: null,
    allProjects: false
  };

  const fallbackSections =
    !details?.sections?.length && pulse?.metrics
      ? [
          {
            id: 'metrics',
            title: 'Key metrics',
            metrics: Object.entries(pulse.metrics)
              .filter(([, v]) => v != null && typeof v === 'number')
              .slice(0, 9)
              .map(([key, value]) => ({
                id: key,
                label: key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()),
                value: String(value),
                tone: 'default'
              }))
          }
        ]
      : [];

  const sections = details?.sections?.length ? details.sections : fallbackSections;

  const handleMetricNavigate = (metric) => {
    if (metric.leadId) {
      const path = buildAiLeadDetailPath(metric.leadId);
      if (path) onNavigate(path);
      return;
    }
    if (metric.aiFilter) {
      onNavigate(buildAiLeadsListPath({ aiFilter: metric.aiFilter, projectId }));
    }
  };

  const handleQuickAction = (action) => {
    if (action.aiFilter) {
      onNavigate(buildAiLeadsListPath({ aiFilter: action.aiFilter, projectId }));
    }
  };

  return (
    <div className="fixed inset-0 z-[1200] flex justify-end bg-black/30">
      <div className="relative flex h-full w-full max-w-lg flex-col bg-white shadow-2xl">
        <div className="border-b border-indigo-100 bg-gradient-to-r from-indigo-50/80 via-white to-teal-50/40 px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <Sparkles className="h-5 w-5 text-[#1E4A53]" />
                <h3 className="text-lg font-semibold text-gray-900">Detailed AI Insights</h3>
                {pulse?.source ? (
                  <AiSourceBadge
                    source={pulse.source}
                    cacheStatus={pulse.cacheStatus}
                    llmInvoked={pulse.llmInvoked}
                  />
                ) : null}
              </div>
              <p className="text-xs text-gray-500">
                {scope?.verticalLabel || 'Dashboard'} ·{' '}
                {scope?.allProjects
                  ? 'All projects'
                  : scope?.projectName || 'Current project'}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-gray-500 hover:bg-white/80">
              <X className="h-5 w-5" />
            </button>
          </div>

          {scope ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {scope.designationName || scope.scopeLabel ? (
                <span className="rounded-full bg-white px-2.5 py-0.5 text-[10px] font-semibold text-gray-700 ring-1 ring-gray-200">
                  {scope.designationName || scope.scopeLabel}
                </span>
              ) : null}
              {scope.levelLabel ? (
                <span className="rounded-full bg-teal-50 px-2.5 py-0.5 text-[10px] font-semibold text-[#1E4A53] ring-1 ring-teal-100">
                  {scope.levelLabel}
                </span>
              ) : null}
              {scope.pulseLevelTier ? (
                <span className="rounded-full bg-indigo-50 px-2.5 py-0.5 text-[10px] font-semibold text-indigo-800 ring-1 ring-indigo-100">
                  {scope.pulseLevelTier} scope
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {pulse?.headline ? (
            <p className="mb-2 text-base font-semibold leading-snug text-gray-900">
              {pulse.headline}
            </p>
          ) : null}
          {pulse?.summaryBox ? (
            <p className="mb-4 text-sm leading-relaxed text-gray-700">{pulse.summaryBox}</p>
          ) : null}

          {details?.portfolioHealth != null ? (
            <div className="mb-5 flex justify-center">
              <PortfolioHealthRing score={details.portfolioHealth} />
            </div>
          ) : null}

          {details?.quickActions?.length ? (
            <div className="mb-5">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Quick actions
              </p>
              <div className="flex flex-wrap gap-2">
                {details.quickActions.map((action) => (
                  <button
                    key={action.id}
                    type="button"
                    onClick={() => handleQuickAction(action)}
                    className="inline-flex items-center gap-1 rounded-full bg-[#1E4A53] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#163a42]">
                    {action.label}
                    <ArrowRight className="h-3 w-3" />
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {sections.map((section) => (
            <div key={section.id} className="mb-5">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {section.title}
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {section.metrics.map((m) => (
                  <MetricTile key={m.id} metric={m} onNavigate={handleMetricNavigate} />
                ))}
              </div>
            </div>
          ))}

          {details?.attentionLeads?.length ? (
            <div className="mb-5">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Leads needing attention
              </p>
              <ul className="space-y-2">
                {details.attentionLeads.map((lead) => (
                  <li key={lead.leadId}>
                    <button
                      type="button"
                      onClick={() => {
                        const path = buildAiLeadDetailPath(lead.leadId);
                        if (path) onNavigate(path);
                      }}
                      className="flex w-full items-start justify-between gap-2 rounded-xl border border-gray-100 bg-white px-3 py-2.5 text-left shadow-sm hover:border-teal-200 hover:bg-teal-50/30">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{lead.leadName}</p>
                        <p className="text-xs text-gray-600">{lead.reason}</p>
                      </div>
                      <span className="shrink-0 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold text-indigo-800">
                        {lead.score}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {details?.priorities?.length ? (
            <div className="mb-5">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Top priorities
              </p>
              <ul className="space-y-2">
                {details.priorities.map((title, idx) => (
                  <li
                    key={`${idx}-${title}`}
                    className="rounded-xl border border-amber-100 bg-amber-50/50 px-3 py-2 text-sm text-amber-950">
                    {title}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {bullets.length ? (
            <div className="mb-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Insight breakdown
              </p>
              <ul className="space-y-2">
                {bullets.map((bullet, idx) => {
                  const Icon = BULLET_ICONS[bullet.category] || Sparkles;
                  return (
                    <li key={idx} className="flex gap-2 text-sm text-gray-700">
                      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#1E4A53]" />
                      <span>{bullet.text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-5 py-4">
          <span className="text-xs text-gray-500">{pulse?.footer?.label}</span>
          <div className="flex items-center gap-2">
            {onRefresh ? (
              <button
                type="button"
                onClick={onRefresh}
                disabled={isRefreshing}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                {isRefreshing ? 'Refreshing…' : 'Refresh'}
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-[#1E4A53] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#163a42]">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
